import { useEffect, useRef } from 'react'
import { useFeedback, type FeedbackTrigger } from './useFeedback'

/**
 * Fire a feedback trigger after a delay, with proper cleanup.
 * Fixes memory leaks in chat.tsx and settings.tsx where setTimeout
 * was used without clearTimeout on unmount.
 *
 * Usage:
 *   useFeedbackTrigger('chat_engaged', 10_000, 'Fik du svar på det du søgte?')
 */
export function useFeedbackTrigger(
  triggerKey: FeedbackTrigger,
  delayMs: number,
  message?: string,
  condition = true,
) {
  const { shouldFireTrigger, openFeedback } = useFeedback()
  const firedRef = useRef(false)

  useEffect(() => {
    if (!condition || firedRef.current) return
    if (!shouldFireTrigger(triggerKey)) return

    firedRef.current = true
    const t = setTimeout(() => openFeedback(triggerKey, message), delayMs)
    return () => clearTimeout(t)
  }, [condition, triggerKey])
}
